import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, PlusCircle } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { deleteAdminPost, listAdminPosts } from "../../lib/adminApi";
import { formatDate, getCategoryLabel } from "../../lib/utils";

const Posts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAdminPosts();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load posts:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this post?")) return;

    try {
      setDeletingId(id);
      await deleteAdminPost(id);
      await load();
    } catch (err) {
      console.error("Delete failed:", err);
      alert(err?.message || "Failed to delete post");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <AdminLayout title="Blog Posts" onRefresh={load}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Manage Posts</h2>
        <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-xl">
          <Link to="/admin/posts/new">
            <PlusCircle className="w-4 h-4 mr-2" />
            New Post
          </Link>
        </Button>
      </div>

      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-stone-600">No posts yet.</div>
          ) : (
            <div className="divide-y">
              {items.map((p) => (
                <div key={p.id} className="p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-900">{p.title || "Untitled Post"}</p>
                    <p className="text-sm text-stone-500">
                      {getCategoryLabel(p.category)} • {formatDate(p.created_at)} • {p.views ?? 0} views
                    </p>
                    <p className="text-sm mt-1 text-stone-600 line-clamp-2">{p.excerpt || "-"}</p>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button asChild variant="outline" className="rounded-xl">
                      <Link to={`/admin/posts/${p.id}/edit`}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => remove(p.id)}
                      disabled={deletingId === p.id}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {deletingId === p.id ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default Posts;
